import React from "react";
import { Link, useRouterState } from "@tanstack/react-router";

const TABS = [
  { label: "Overview", to: "/admin" },
  { label: "Users", to: "/admin/users" },
  { label: "Usage", to: "/admin/usage" },
  { label: "Revenue", to: "/admin/revenue" },
  { label: "Pipeline", to: "/admin/pipeline" },
  { label: "Funnel", to: "/admin/funnel" },
  { label: "Store", to: "/admin/store" },
  { label: "Collections", to: "/admin/collections" },
  { label: "Achievements", to: "/admin/achievements" },
  { label: "Events", to: "/admin/events" },
  { label: "Social", to: "/admin/social" },
  { label: "Groups", to: "/admin/groups" },
  { label: "Feedback", to: "/admin/feedback" },
  { label: "Announcements", to: "/admin/announcements" },
  { label: "Emails", to: "/admin/emails" },
  { label: "Plan keys", to: "/admin/keys" },
  { label: "Partners", to: "/admin/partners" },
  { label: "Invites", to: "/admin/referrals" },
  { label: "Flags", to: "/admin/flags" },
  { label: "Audit", to: "/admin/audit" },
];

// Shared admin section chrome: topo header + segmented tab sub-nav.
// Each admin page renders its content inside this shell.
export function AdminShell({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { location } = useRouterState();
  const pathname = location.pathname;

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="atlas-topo" style={{ position: "relative", padding: "28px 28px 0" }}>
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <div>
            <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Admin</p>
            <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
              {title}
            </h1>
          </div>
          {action}
        </div>

        {/* ── Tab sub-nav ──────────────────────────────────────────────── */}
        <nav
          className="r-scroll-x"
          style={{
            position: "relative",
            display: "flex",
            gap: 4,
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          {TABS.map((tab) => {
            const active = pathname === tab.to;
            return (
              <Link
                key={tab.to}
                to={tab.to}
                style={{
                  padding: "8px 14px 10px",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--text-primary)" : "var(--text-muted)",
                  textDecoration: "none",
                  borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                  transition: "color 0.15s ease, border-color 0.15s ease",
                }}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div style={{ padding: "20px 28px 28px", display: "flex", flexDirection: "column", gap: 20 }}>
        {children}
      </div>
    </div>
  );
}

export function AccessDenied() {
  return (
    <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Access denied</p>
    </div>
  );
}
